import type { PlaylistDto, PlaylistsState } from "@piano/shared";
import { api } from "../api/index.js";
import { ui } from "./ui.svelte.js";

/** Setlists des Nutzers – serverseitig die einzige Wahrheit, live per SSE aktualisiert. */
class Playlists {
  list = $state<PlaylistDto[]>([]);
  activeId = $state<string | null>(null);
  loaded = $state(false);

  byId = $derived(new Map(this.list.map((p) => [p.id, p])));
  active = $derived(this.activeId ? (this.byId.get(this.activeId) ?? null) : null);

  apply(state: PlaylistsState): void {
    this.list = state.playlists;
    this.activeId = state.activeId;
    this.loaded = true;
  }

  async load(): Promise<void> {
    this.apply(await api.playlists());
  }

  private async run(action: Promise<PlaylistsState>, success?: string): Promise<boolean> {
    try {
      this.apply(await action);
      if (success) ui.toast(success, "success");
      return true;
    } catch (error) {
      ui.error(error);
      await this.load().catch(() => undefined);
      return false;
    }
  }

  async create(name: string, documentIds: string[] = []): Promise<string | null> {
    const ok = await this.run(api.createPlaylist({ name, documentIds }), `Setlist „${name}“ angelegt`);
    return ok ? this.activeId : null;
  }

  update(id: string, input: { name?: string; icon?: string; accentColor?: string }) {
    return this.run(api.updatePlaylist(id, input));
  }

  remove(id: string) {
    return this.run(api.deletePlaylist(id), "Setlist gelöscht");
  }

  setActive(id: string, quiet = false) {
    const name = this.byId.get(id)?.name ?? "";
    return this.run(
      api.setActivePlaylist(id),
      quiet ? undefined : `„${name}“ ist jetzt die aktuelle Setlist`,
    );
  }

  async add(id: string, documentId: string): Promise<void> {
    const playlist = this.byId.get(id);
    if (playlist?.items.includes(documentId)) {
      ui.toast(`Schon in „${playlist.name}“`);
      return;
    }
    await this.run(
      api.addToPlaylist(id, [documentId]),
      playlist ? `Zu „${playlist.name}“ hinzugefügt` : undefined,
    );
  }

  /** Optimistisches Umsortieren (Drag & Drop), danach Abgleich mit dem Server. */
  async reorder(id: string, documentIds: string[]): Promise<void> {
    // Das gerade gespielte Stück bleibt das aktuelle – auch an neuer Position.
    this.list = this.list.map((p) => {
      if (p.id !== id) return p;
      const current = p.items[p.currentIndex];
      const index = current ? documentIds.indexOf(current) : -1;
      return { ...p, items: documentIds, currentIndex: index >= 0 ? index : p.currentIndex };
    });
    await this.run(api.setPlaylistItems(id, documentIds));
  }

  async removeItem(id: string, documentId: string): Promise<void> {
    this.list = this.list.map((p) =>
      p.id === id ? { ...p, items: p.items.filter((item) => item !== documentId) } : p,
    );
    await this.run(api.removeFromPlaylist(id, documentId));
  }

  setCurrent(id: string, index: number) {
    this.list = this.list.map((p) => (p.id === id ? { ...p, currentIndex: index } : p));
    return this.run(api.setCurrentIndex(id, index));
  }

  containing(documentId: string): PlaylistDto[] {
    return this.list.filter((p) => p.items.includes(documentId));
  }

  reset(): void {
    this.list = [];
    this.activeId = null;
    this.loaded = false;
  }
}

export const playlists = new Playlists();
